import { useMemo, useState } from 'react';
import { useMutation } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import { Field } from '../../../components/Field.jsx';
import { REGISTRAR_EGRESO, REGISTRAR_INGRESO, TRANSFERIR_STOCK } from './gql.js';

const emptyItem = () => ({ productoId: '', descripcion: '', cantidad: 1, costoUnit: 0 });

const titles = {
  ingreso: 'Registrar ingreso',
  egreso: 'Registrar egreso',
  transferencia: 'Transferir stock',
};

const subtitles = {
  ingreso: 'Actualizá el stock disponible registrando ingresos por compras, ajustes o inventarios.',
  egreso: 'Controlá las salidas de stock por ventas, ajustes o transferencias.',
  transferencia: 'Mové stock entre sucursales manteniendo la trazabilidad en el Kardex.',
};

export default function MovimientoForm({ mode = 'ingreso', onCancel, onSaved }) {
  const [items, setItems] = useState([emptyItem()]);
  const [errors, setErrors] = useState({});
  const [formState, setFormState] = useState({
    origenSucursalId: '',
    destinoSucursalId: '',
    proveedorId: '',
    fecha: new Date().toISOString().slice(0, 16),
    usuario: '',
    tipo: mode === 'egreso' ? 'VENTA' : mode === 'transferencia' ? 'TRANSFERENCIA' : 'COMPRA',
    observacion: '',
  });
  const [, registrarIngreso] = useMutation(REGISTRAR_INGRESO);
  const [, registrarEgreso] = useMutation(REGISTRAR_EGRESO);
  const [, transferirStock] = useMutation(TRANSFERIR_STOCK);

  const handleItemChange = (index, field, value) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [field]: field === 'descripcion' ? value : Number(value),
      };
      return next;
    });
  };

  const handleAddItem = () => setItems((prev) => [...prev, emptyItem()]);
  const handleRemoveItem = (index) => setItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const total = useMemo(() => items.reduce((acc, item) => acc + (Number(item.cantidad) || 0) * (Number(item.costoUnit) || 0), 0), [items]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (mode !== 'ingreso' && !formState.origenSucursalId) {
      nextErrors.origenSucursalId = 'Indicá la sucursal de origen.';
    }
    if (mode === 'ingreso' && !formState.destinoSucursalId) {
      nextErrors.destinoSucursalId = 'Seleccioná la sucursal de destino.';
    }
    if (mode === 'transferencia' && !formState.destinoSucursalId) {
      nextErrors.destinoSucursalId = 'Seleccioná la sucursal destino.';
    }
    if (!formState.usuario.trim()) {
      nextErrors.usuario = 'El usuario es obligatorio.';
    }
    const validItems = items.filter((item) => item.descripcion.trim());
    if (!validItems.length) {
      nextErrors.items = 'Agregá al menos un ítem con descripción.';
    }
    const invalidProducto = validItems.some((item) => !Number.isInteger(Number(item.productoId)) || Number(item.productoId) <= 0);
    if (invalidProducto) {
      nextErrors.items = 'Cada ítem debe indicar un producto válido.';
    }
    const invalidCantidad = validItems.some((item) => Number(item.cantidad) <= 0);
    if (invalidCantidad) {
      nextErrors.items = 'Las cantidades deben ser mayores a cero.';
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    try {
      if (mode === 'ingreso') {
        await registrarIngreso({
          input: {
            sucursalId: String(formState.destinoSucursalId),
            proveedorId: formState.proveedorId ? String(formState.proveedorId) : null,
            fecha: formState.fecha,
            tipo: formState.tipo,
            observacion: formState.observacion.trim() || null,
            usuario: formState.usuario.trim(),
            items: validItems.map((item) => ({
              productoId: String(Number(item.productoId)),
              descripcion: item.descripcion.trim(),
              cantidad: Number(item.cantidad) || 0,
              costoUnit: Number(item.costoUnit) || 0,
            })),
          },
        });
      } else if (mode === 'egreso') {
        await registrarEgreso({
          input: {
            sucursalId: String(formState.origenSucursalId),
            destinoSucursalId: formState.destinoSucursalId ? String(formState.destinoSucursalId) : null,
            fecha: formState.fecha,
            tipo: formState.tipo,
            observacion: formState.observacion.trim() || null,
            usuario: formState.usuario.trim(),
            items: validItems.map((item) => ({
              productoId: String(Number(item.productoId)),
              descripcion: item.descripcion.trim(),
              cantidad: Number(item.cantidad) || 0,
              costoUnit: Number(item.costoUnit) || 0,
            })),
          },
        });
      } else {
        await transferirStock({
          input: {
            origenSucursalId: String(formState.origenSucursalId),
            destinoSucursalId: String(formState.destinoSucursalId),
            fecha: formState.fecha,
            usuario: formState.usuario.trim(),
            observacion: formState.observacion.trim() || null,
            items: validItems.map((item) => ({
              productoId: String(Number(item.productoId)),
              descripcion: item.descripcion.trim(),
              cantidad: Number(item.cantidad) || 0,
              costoUnit: Number(item.costoUnit) || 0,
            })),
          },
        });
      }
      onSaved?.();
    } catch (error) {
      setErrors({ form: error.message });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader title={titles[mode]} subtitle={subtitles[mode]} />

      {errors.form ? (
        <div className="rounded-2xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">{errors.form}</div>
      ) : null}

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="grid grid-cols-12 gap-4">
          {mode !== 'ingreso' ? (
            <Field label="Sucursal origen" hint={errors.origenSucursalId}>
              <input
                value={formState.origenSucursalId}
                onChange={(event) => setFormState((prev) => ({ ...prev, origenSucursalId: event.target.value }))}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </Field>
          ) : null}
          {(mode === 'ingreso' || mode === 'transferencia') ? (
            <Field label="Sucursal destino" hint={errors.destinoSucursalId}>
              <input
                value={formState.destinoSucursalId}
                onChange={(event) => setFormState((prev) => ({ ...prev, destinoSucursalId: event.target.value }))}
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </Field>
          ) : null}
          {mode === 'ingreso' ? (
            <Field label="Proveedor">
              <input
                value={formState.proveedorId}
                onChange={(event) => setFormState((prev) => ({ ...prev, proveedorId: event.target.value }))}
                placeholder="Opcional"
                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              />
            </Field>
          ) : null}
          <Field label="Fecha y hora">
            <input
              type="datetime-local"
              value={formState.fecha}
              onChange={(event) => setFormState((prev) => ({ ...prev, fecha: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <Field label="Tipo de movimiento">
            <select
              value={formState.tipo}
              onChange={(event) => setFormState((prev) => ({ ...prev, tipo: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            >
              {mode === 'ingreso' ? (
                <>
                  <option value="COMPRA">Compra</option>
                  <option value="AJUSTE">Ajuste</option>
                  <option value="INVENTARIO">Inventario</option>
                  <option value="TRANSFERENCIA">Transferencia</option>
                </>
              ) : mode === 'egreso' ? (
                <>
                  <option value="VENTA">Venta</option>
                  <option value="AJUSTE">Ajuste</option>
                  <option value="TRANSFERENCIA">Transferencia</option>
                </>
              ) : (
                <option value="TRANSFERENCIA">Transferencia</option>
              )}
            </select>
          </Field>
          <Field label="Usuario" hint={errors.usuario}>
            <input
              value={formState.usuario}
              onChange={(event) => setFormState((prev) => ({ ...prev, usuario: event.target.value }))}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
          <Field label="Observación">
            <textarea
              value={formState.observacion}
              onChange={(event) => setFormState((prev) => ({ ...prev, observacion: event.target.value }))}
              rows={3}
              className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
            />
          </Field>
        </div>
      </section>

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Ítems</h2>
          <button
            type="button"
            onClick={handleAddItem}
            className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-3 py-1.5 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Agregar ítem
          </button>
        </div>
        {errors.items ? <p className="text-sm text-red-300">{errors.items}</p> : null}
        <div className="flex flex-col gap-4">
          {items.map((item, index) => (
            <div key={index} className="grid grid-cols-12 gap-3 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--subpanel),0.4)] p-4">
              <div className="col-span-12 md:col-span-4">
                <label className="mb-1 block text-xs font-medium text-muted">Descripción</label>
                <input
                  value={item.descripcion}
                  onChange={(event) => handleItemChange(index, 'descripcion', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-muted">Producto ID</label>
                <input
                  value={item.productoId}
                  onChange={(event) => handleItemChange(index, 'productoId', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-muted">Cantidad</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.cantidad}
                  onChange={(event) => handleItemChange(index, 'cantidad', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2">
                <label className="mb-1 block text-xs font-medium text-muted">Costo unitario</label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={item.costoUnit}
                  onChange={(event) => handleItemChange(index, 'costoUnit', event.target.value)}
                  className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </div>
              <div className="col-span-6 md:col-span-2 flex items-end">
                <button
                  type="button"
                  onClick={() => handleRemoveItem(index)}
                  className="w-full rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-200 transition hover:bg-red-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                  disabled={items.length === 1}
                >
                  Quitar
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 text-right text-sm font-semibold text-[rgb(var(--fg))]">
          Total estimado:{' '}
          {new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(total)}
        </div>
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgb(var(--border))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
        }
        right={
          <button
            type="submit"
            className="rounded-xl bg-[rgb(var(--brand))] px-6 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Confirmar movimiento
          </button>
        }
      />
    </form>
  );
}
